import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Card, Table, Button, Icons } from "@pankod/refine-antd";
import { useCustom } from "@pankod/refine-core";
import dayjs from "dayjs";
import ENV from "utils/env";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { extractPaginated } from "../utils";

type RawProductClick = {
  productName?: string;
  productCode?: string;
  count: string;
  lastClickedAt?: string;
};

interface ProductClicksTableProps {
  dateRange: [string, string];
  shopCode: string | null;
}

export const ProductClicksTable: React.FC<ProductClicksTableProps> = ({
  dateRange,
  shopCode,
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => {
    setPage(1);
  }, [dateRange, shopCode]);

  const { data: response, isFetching } = useCustom({
    url: `${ENV.API_URL}/api/analytics/stats/top-products`,
    method: "get",
    config: {
      query: {
        startDate: dateRange[0],
        endDate: dateRange[1],
        page,
        limit: pageSize,
        ...(shopCode ? { shopCode } : {}),
      },
    },
    queryOptions: { retry: 0 },
  });

  const { items, total } = extractPaginated<RawProductClick>(response?.data);

  const dataSource = items.map((r, i) => ({
    key: (page - 1) * pageSize + i,
    productName: r.productName || "Bilinmeyen",
    productCode: r.productCode || "-",
    count: parseInt(r.count, 10),
    lastClickedAt: r.lastClickedAt
      ? dayjs(r.lastClickedAt).format("DD/MM/YYYY HH:mm")
      : "-",
  }));

  const columns = useMemo(
    () => [
      {
        title: "Ürün Adı",
        dataIndex: "productName",
        key: "productName",
        sorter: (a: any, b: any) => a.productName.localeCompare(b.productName),
      },
      {
        title: "Ürün Kodu",
        dataIndex: "productCode",
        key: "productCode",
      },
      {
        title: "Tıklama Sayısı",
        dataIndex: "count",
        key: "count",
        sorter: (a: any, b: any) => a.count - b.count,
        defaultSortOrder: "descend" as const,
      },
      {
        title: "Son Tıklanma",
        dataIndex: "lastClickedAt",
        key: "lastClickedAt",
      },
    ],
    []
  );

  const handleExport = useCallback(() => {
    const ws = XLSX.utils.json_to_sheet(dataSource.map(({ key, ...rest }) => rest));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ürün Tıklama");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf], { type: "application/octet-stream" }), "urun-tiklama.xlsx");
  }, [dataSource]);

  return (
    <Card
      title="Ürün Tıklama Detayı"
      extra={
        <Button
          icon={<Icons.DownloadOutlined />}
          onClick={handleExport}
          disabled={dataSource.length === 0}
        >
          İndir
        </Button>
      }
    >
      <Table
        dataSource={dataSource}
        columns={columns}
        loading={isFetching}
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
          showSizeChanger: true,
          showTotal: (t) => `Toplam ${t.toLocaleString("tr-TR")} ürün`,
        }}
        size="small"
        scroll={{ x: 600 }}
      />
    </Card>
  );
};
